import { api } from '@/shared/api/http'
import type { ProformaStatus } from '@/features/billing/proforma/types'

/**
 * What is waiting for the signed-in user.
 *
 * Counts the user has no permission to see come back missing rather than zero:
 * a zero would tell someone "nothing is waiting" when the truth is "you cannot
 * see this queue". Missing counts render no tile at all.
 */
export interface PortalSummary {
  waitingOnMe?: number
  myDrafts?: number
  myInReview?: number
  myReturned?: number
  recent: RecentDocument[]
  /** The user's own returned documents with the approver's reason, newest first. */
  returned: ReturnedDocument[]
  /** Their documents dated this month, counted by status. Absent without permission. */
  pipeline?: Pipeline
}

export interface ReturnedDocument {
  id: number
  docNo?: string
  customerName?: string
  currency?: string
  totalAmt?: number
  /** Missing only for documents returned before the approval trail existed. */
  returnedBy?: string
  returnedAt?: string
  note?: string
}

export interface Pipeline {
  /** First day of the month, inclusive (yyyy-MM-dd). */
  from: string
  /** First day of the next month, exclusive. */
  to: string
  byStatus: Partial<Record<ProformaStatus, number>>
}

export interface RecentDocument {
  id: number
  docNo?: string
  status: ProformaStatus
  customerName?: string
  currency?: string
  totalAmt?: number
  updatedAt?: string
}

export const portalKeys = {
  all: ['portal'] as const,
  summary: ['portal', 'summary'] as const,
}

/**
 * `returned` is filled in when absent: a backend older than the field must
 * leave Beranda without that card, not crash it. Deploys of the two apps are
 * not atomic.
 */
export async function fetchPortalSummary(): Promise<PortalSummary> {
  const summary = await api.get<PortalSummary>('/portal/summary')
  return { ...summary, recent: summary.recent ?? [], returned: summary.returned ?? [] }
}
